import type {
  ContentSlotConfig,
  DetailsSlotConfig,
  HeaderSlotConfig,
  SidebarSlotConfig,
} from './slot-config.js';
import type { DetailsLayoutContextValue } from '../details-layout/DetailsLayoutContext.js';

/** @internal */
export interface SlotRegistryState {
  header: HeaderSlotConfig | null;
  sidebar: SidebarSlotConfig | null;
  content: ContentSlotConfig | null;
  details: DetailsSlotConfig | null;
  /**
   * Bridge: `DetailsLayoutInner` publishes its context value here so
   * `PageLayoutDetails` can re-provide it around its portal. The provider
   * placed by `DetailsLayoutInner` only covers the split/overlay DOM it
   * renders, not the portaled `PageLayout.Details` children which live in a
   * different React subtree. This is a context value, not a slot config; it
   * piggybacks on the registry's pub/sub to avoid a second primitive.
   */
  detailsLayoutContext: DetailsLayoutContextValue | null;
}

/** @internal */
export interface SlotRegistry {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => SlotRegistryState;
  register: <K extends keyof SlotRegistryState>(
    slot: K,
    config: NonNullable<SlotRegistryState[K]>,
  ) => void;
  unregister: (slot: keyof SlotRegistryState) => void;
}

const INITIAL_STATE: SlotRegistryState = {
  header: null,
  sidebar: null,
  content: null,
  details: null,
  detailsLayoutContext: null,
};

/** @internal */
export function createSlotRegistry(): SlotRegistry {
  let state: SlotRegistryState = INITIAL_STATE;
  let frozenSnapshot: SlotRegistryState = INITIAL_STATE;
  const listeners = new Set<() => void>();

  function notify() {
    frozenSnapshot = Object.freeze({ ...state });
    for (const listener of listeners) {
      listener();
    }
  }

  const subscribe: SlotRegistry['subscribe'] = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const getSnapshot: SlotRegistry['getSnapshot'] = () => frozenSnapshot;

  const register: SlotRegistry['register'] = (slot, config) => {
    if (state[slot] === config) {
      // No-op: identical reference already registered. Avoid notifying
      // subscribers (and re-rendering MainShell/HeaderShell) for unchanged
      // input. Callers are expected to memoize the registered config
      // (see portal-slots.tsx).
      return;
    }
    state = { ...state, [slot]: config };
    notify();
  };

  const unregister: SlotRegistry['unregister'] = (slot) => {
    state = { ...state, [slot]: null };
    notify();
  };

  return { subscribe, getSnapshot, register, unregister };
}
